import { supabase } from '../supabase';

export type InvoiceTermCode = 'due_on_receipt' | 'net_7' | 'net_15' | 'net_30' | 'net_60' | 'custom';

export const TERM_DAYS: Record<string, number> = {
  due_on_receipt: 0,
  net_7: 7,
  net_15: 15,
  net_30: 30,
  net_60: 60,
};

export const TERM_LABELS: Record<string, string> = {
  due_on_receipt: 'Due on receipt',
  net_7: 'Net 7',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_60: 'Net 60',
  custom: 'Custom',
};

/** Selectable default terms (no "custom" — custom is per-invoice only). */
export const DEFAULT_TERM_OPTIONS: { value: InvoiceTermCode; label: string }[] = [
  { value: 'due_on_receipt', label: 'Due on receipt' },
  { value: 'net_7', label: 'Net 7' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
];

export function termLabel(code: string | null | undefined): string {
  if (!code) return '';
  return TERM_LABELS[code] || code;
}

/** Compute a due date (ISO) from an anchor date + a Net term. Returns null for invalid input. */
export function computeDueDate(anchor: string | Date | null | undefined, term: InvoiceTermCode): string | null {
  if (!anchor) return null;
  const base = anchor instanceof Date ? new Date(anchor.getTime()) : new Date(anchor);
  if (Number.isNaN(base.getTime())) return null;
  const days = TERM_DAYS[term] ?? 0;
  base.setDate(base.getDate() + days);
  return base.toISOString();
}

/**
 * Override the payment terms on a single invoice. For a Net term, due_at is
 * recomputed from the anchor (invoice issue date). For 'custom', pass an explicit
 * dueAt date and term is stored as 'custom'.
 */
export async function updateInvoiceTerms(params: {
  invoiceId: string;
  term: InvoiceTermCode;
  anchor?: string | Date | null;
  customDueAt?: string | null;
}): Promise<void> {
  const { invoiceId, term, anchor, customDueAt } = params;
  const due_at = term === 'custom' ? (customDueAt || null) : computeDueDate(anchor, term);
  const { error } = await supabase
    .from('invoices')
    .update({ payment_terms: term, due_at })
    .eq('id', invoiceId);
  if (error) throw error;
}
